<template>
  <div>
    <row-col-text-field
      :value="dataset.title"
      :label="$t('form.dataset_title')"
      :rules="rules.char_required_max_250"
      :required="true"
      :readonly="readonly"
      @input="update('title', $event)"
    />
    <MultilingualTextField
      :value="dataset.short_description"
      :label="$t('form.dataset_short_description')"
      :rules="rules.char_max_180"
      :required="true"
      :readonly="readonly"
      entity-type="dataset"
      field="short_description"
      @input="update('short_description', $event)"
    />

    <div class="mt-4">
      <MultilingualTextField
        :value="dataset.description"
        :label="$t('form.dataset_description')"
        :readonly="readonly"
        :rich="true"
        :editor-ready="editorReady"
        entity-type="dataset"
        field="description"
        @input="update('description', $event)"
      />
    </div>

    <v-row class="mt-4">
      <col-date-field
        :value="dataset.publication_date"
        :label="$t('form.dataset_publication_date')"
        :readonly="readonly"
        @input="update('publication_date', $event)"
      />
    </v-row>

    <row-col-select-multiple-field
      :value="dataset.licenses"
      data-source="dataset_license"
      :label="$t('form.dataset_licenses')"
      :readonly="readonly"
      @input="update('licenses', $event)"
    />

    <div class="mb-3">
      <h5 class="mb-2">{{ $t("form.keywords") }}</h5>
      <ArrayFieldForm
        v-if="!readonly"
        :value="dataset.keywords"
        name-field="keywords"
        :label="$t('form.keywords')"
        @input="update('keywords', $event)"
      />
      <v-text-field v-else :value="displayKeywords" disabled />
    </div>

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-3">{{ $t("form.dataset_access_section") }}</h5>
    <v-row>
      <v-col cols="12" md="6" class="py-0">
        <h5>{{ $t("form.dataset_access_type") }}</h5>
        <v-select
          :value="dataset.access_type"
          :items="accessTypes"
          item-text="text"
          item-value="value"
          :readonly="readonly"
          :disabled="readonly"
          outlined
          @input="update('access_type', $event)"
        />
      </v-col>
    </v-row>
    <div v-if="showAccessInstructions" class="mt-4">
      <h5 class="mb-2">
        {{ $t("form.dataset_access_instructions") }}
        <v-chip v-if="needsInstructions && !readonly" class="ma-2" x-small outlined color="primary">
          {{ $t("form.mandatory") }}
        </v-chip>
      </h5>
      <EditorText
        v-if="editorReady"
        :text="dataset.access_instructions"
        language="access_instructions"
        field="access_instructions"
        :auto-grow="true"
        :disabled="readonly"
        @change-content="updateAccessInstructions"
      />
    </div>
    <v-input
      v-if="!readonly && showAccessInstructions"
      :value="accessInstructionsValidation"
      :rules="[value => value === true || value]"
    />

    <v-row dense>
      <v-col cols="12" md="6" class="py-0" v-for="field in linkFields" :key="field">
        <row-col-text-field
          :value="dataset[field]"
          :label="$t(`form.${field}`)"
          :rules="rules.uriRules"
          :readonly="readonly"
          @input="update(field, $event)"
        >
          <template v-slot:append-outer>
            <v-tooltip v-if="isOpenableUrl(dataset[field])" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  :href="dataset[field]"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="$t('form.open_link')"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </template>
              {{ $t("form.open_link") }}
            </v-tooltip>
          </template>
        </row-col-text-field>
      </v-col>
    </v-row>
    <v-input
      v-if="!readonly"
      :value="openAccessValid"
      :rules="[value => value || $t('form.dataset_open_link_required')]"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-3">{{ $t("form.dataset_identifiers_section") }}</h5>
    <v-row>
      <v-col cols="12" md="6" class="py-0">
        <row-col-text-field
          :value="dataset.doi"
          label="DOI"
          :rules="rules.doiRules"
          :readonly="readonly"
          @input="update('doi', $event)"
        />
      </v-col>
    </v-row>
    <row-col-text-area-field
      :value="dataset.preferred_citation"
      :label="$t('form.dataset_preferred_citation')"
      :rules="rules.char_max_2000"
      :readonly="readonly"
      @input="update('preferred_citation', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5>{{ $t("form.dataset_relations_section") }}</h5>
    <row-col-select-one-field
      :value="dataset.responsible_person"
      data-source="person"
      :label="$t('form.dataset_responsible_person')"
      :required="true"
      :readonly="readonly"
      @input="update('responsible_person', $event)"
    />
    <row-col-select-multiple-field
      v-for="relation in relationFields"
      :key="relation.field"
      :value="dataset[relation.field]"
      :data-source="relation.source"
      :label="$t(relation.label)"
      :required="relation.required"
      :readonly="readonly"
      @input="update(relation.field, $event)"
    />
  </div>
</template>

<script>
import validationRules from "@/mixins/validationRules";
import ArrayFieldForm from "@/components/reusables/ArrayFieldForm.vue";
import ColDateField from "@/components/reusables/ColDateField.vue";
import EditorText from "@/components/reusables/EditorText.vue";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import RowColSelectMultipleField from "@/components/reusables/RowColSelectMultipleField.vue";
import RowColSelectOneField from "@/components/reusables/RowColSelectOneField.vue";
import RowColTextAreaField from "@/components/reusables/RowColTextAreaField.vue";
import RowColTextField from "@/components/reusables/RowColTextField.vue";

export default {
  name: "DatasetFields",
  mixins: [validationRules],
  components: {
    ArrayFieldForm,
    ColDateField,
    EditorText,
    MultilingualTextField,
    RowColSelectMultipleField,
    RowColSelectOneField,
    RowColTextAreaField,
    RowColTextField
  },
  props: {
    dataset: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    editorReady: { type: Boolean, default: true }
  },
  data: () => ({
    linkFields: ["hugging_face_url", "download_url", "website_url"],
    relationFields: [
      { field: "researchers", source: "person", label: "form.dataset_researchers", required: true },
      { field: "publications", source: "publication", label: "form.dataset_publications" },
      { field: "projects", source: "project", label: "form.dataset_projects" },
      { field: "research_areas", source: "research_area", label: "form.dataset_research_areas" },
      { field: "softwares", source: "software", label: "form.dataset_softwares" }
    ]
  }),
  computed: {
    accessTypes() {
      return ["open", "restricted", "agreement", "unavailable"].map(value => ({
        value,
        text: this.$t(`form.dataset_access_types.${value}`)
      }));
    },
    needsInstructions() {
      return ["restricted", "agreement"].includes(this.dataset.access_type);
    },
    showAccessInstructions() {
      return this.dataset.access_type !== "open" || Boolean(this.dataset.access_instructions);
    },
    accessInstructionsValidation() {
      const value = this.dataset.access_instructions || "";
      const plainText = value
        .replace(/<br\s*\/?>(\r?\n)?/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
      if (this.needsInstructions && !plainText) {
        return this.$t("forms.field.validation.required");
      }
      if (value.length > 2000) {
        return this.$t("forms.field.validation.char.max", { max: 2000 });
      }
      return true;
    },
    openAccessValid() {
      return this.dataset.access_type !== "open" ||
        Boolean(this.dataset.download_url || this.dataset.hugging_face_url);
    },
    displayKeywords() {
      const values = (this.dataset.keywords || []).filter(Boolean);
      return values.length ? values.join(", ") : "-";
    }
  },
  methods: {
    isOpenableUrl(url) {
      if (!url || typeof url !== "string") {
        return false;
      }
      try {
        const parsedUrl = new URL(url);
        return ["http:", "https:"].includes(parsedUrl.protocol);
      } catch (error) {
        return false;
      }
    },
    update(field, value) {
      this.$emit("update", field, value);
    },
    updateAccessInstructions(_language, content) {
      this.update("access_instructions", content || "");
    }
  }
};
</script>
